import * as $ from 'jquery';
import { disposableSubscription, Lazy } from 'common/barrels/core';
import { Subject, Observable } from 'common/barrels/rx';
import { Map } from 'immutable';

import { ContainerControl } from 'runtime/controls/containercontrol';
import { ControlArgs } from 'runtime/controls/controlargs';
import { registerBinding } from 'runtime/bindingengine';
import { ViewInfoRequested } from 'runtime/events/viewinforequested';
import { TabItemPanelContainer } from 'runtime/controls/tabitempanelcontainer';
import * as rxui from 'common/rxui/core';

import 'runtime/styles/controls/tabcontainer.less';

// note: tooltip hasn't been missed, it has been deliberately removed as a feature. It will eventually be removed from Studio.

export class TabContainer extends ContainerControl {
    private tabs: TabItemPanelContainer[];
    private tabList: TabItemPanelContainer[] = [];
    private tabMap = Map<string, TabDetails>();

    private $tabHeaders: JQuery;
    private $tabContent: JQuery;

    private tabRequestedSubject: Subject<string>;

    private selectedTab: string;

    private viewInfo = new Lazy(() => this.eventAggregator.publish(new ViewInfoRequested()));

    constructor(args: ControlArgs) {
        super(args);

        this.$element.data('tabcontainer', this);

        this.methods.register(this.config.elementName, this);
            
        this.bindElements(this.$element.children(), this);

        this.initializeDom();
            
        this.$element.children('div').toArray().map(x => $(x).data('control')).filter(x => x instanceof TabItemPanelContainer).map(x => <TabItemPanelContainer>x).forEach(x => x.registerTab(this));

        this.tabRequestedSubject = new Subject<string>();

        var initiallySelectedTab = this.selectableTabs().find(x => true);

        var hasFocus = () => this.$element.is(':focus');

        this.subscriptions = disposableSubscription(
            this.tabRequestedSubject.distinctUntilChanged().pairwise().subscribe(x => this.changeActiveTab(x[0], x[1])),
            rxui.filteredKeys(this.$element, [$.ui.keyCode.LEFT, $.ui.keyCode.UP]).filter(() => hasFocus()).subscribe(() => this['select Prev Tab']()),
            rxui.filteredKeys(this.$element, [$.ui.keyCode.RIGHT, $.ui.keyCode.DOWN]).filter(() => hasFocus()).subscribe(() => this['select Next Tab']()),
            this.tabRequestedSubject,
            this.processBindings(this.config)
        );

        this.tabRequestedSubject.next(null);

        if (initiallySelectedTab !== null)
            this.tabRequestedSubject.next(initiallySelectedTab.tabItem());

        this.initialize();
    }

    private changeActiveTab(oldTabName: string, newTabName: string) {
        if (oldTabName !== null) {
            var oldTab = this.tabMap.get(oldTabName);
            oldTab.$panel.hide();
            oldTab.$header.removeClass('tabcontainer-activetab');
            oldTab.isActivePropagator(false);
        }
            
        var newTab = this.tabMap.get(newTabName);
        newTab.$panel.show();
        newTab.$header.addClass('tabcontainer-activetab');
        newTab.isActivePropagator(true);
        newTab.$panel.triggerHandler('events.Opened');

        this.selectedTab = newTabName;

        this.$tabHeaders.scrollTop(this.$tabHeaders.scrollTop() + $(newTab.$header[0]).position().top);
    }

    private selectableTabs() {
        return this.tabList.filter(x => {
            const tab = this.tabMap.get(x.tabItem());
            return tab.enabled !== false && tab.visible !== false;
        });
    }

    private initializeDom() {
        this.$element.addClass('control-container tabcontainer');
        this.$tabHeaders = $('<ul />').appendTo(this.$element);
        this.$tabContent = $('<div />').appendTo(this.$element);
        this.installScrollVeto(this.$tabContent);
    }

    private navigateTab(offset: (items: TabItemPanelContainer[]) => number) {
        var items = this.selectableTabs();

        if (items.length === 0)
            return;

        var index = items.map(x => x.tabItem()).indexOf(this.selectedTab);
        var item = index === -1
            ? items[0]
            : items.concat(items).slice(index).slice(offset(items))[0];

        this.tabRequestedSubject.next(item.tabItem());
    }

    'select Next Tab'() {
        this.navigateTab(() => 1);
    }

    'select Prev Tab'() {
        this.navigateTab(x => x.length - 1);
    }

    'select Tab By Name'(name: string) {
        if (this.selectableTabs().map(x => x.tabItem()).some(x => x === name))
            this.tabRequestedSubject.next(name);
    }

    registerTab(name: string, languageKey: string, $content: JQuery, control: TabItemPanelContainer, initialEnabled: boolean, initialVisible: boolean, isActivePropagator: (x: boolean) => void, visibilityPropagator: (x: boolean) => void) {
        var $a = $('<a />').bindText(languageKey ? this.resources.getString(this.viewInfo.value().definitionName, languageKey) : name);
        var $header = $('<li />').append($a).on('click', () => this['select Tab By Name'](name));
        this.$tabHeaders.append($header);
        this.$tabContent.append($content.hide());

        this.tabMap = this.tabMap.set(name, new TabDetails($header, $content, initialEnabled, initialVisible, isActivePropagator, visibilityPropagator));
        this.tabList.push(control);
        this.tabEnabledChanged(name, initialEnabled);
        this.tabVisibleChanged(name, initialVisible);
    }

    tabEnabledChanged(name: string, value: boolean) {
        var tab = this.tabMap.get(name);
        tab.enabled = value;
        tab.$header.toggleClass('tabcontainer-disabledtab', value === false);
    }

    tabVisibleChanged(name: string, value: boolean) {
        var tab = this.tabMap.get(name);
        tab.visible = value;
            
        if (value === false)
            tab.$header.hide();
        else
            tab.$header.show();

        tab.visibilityPropagator(value);
    }

    focus() {
        this.setFocusToFirstFocusableSubElement(this.$tabContent.children(':visible'));
    }

    applyEnabledState(value: boolean) {
        this.propagateEnabledStateToChildElements(this.$tabContent, this.writableAgnosticDerivedEnabledState())
    }

    tabIndexElements() {
        return this.$element;
    }

    resizeStream(): Observable<{}> {
        return this.stretches() ? this.container.resizeStream() : Observable.never();
    }
}

class TabDetails {
    constructor(public $header: JQuery, public $panel: JQuery, public enabled: boolean, public visible: boolean, public isActivePropagator: (x: boolean) => void, public visibilityPropagator: (x: boolean) => void) {}
}

registerBinding('tabcontainer', TabContainer);